import { Component, Input, OnDestroy, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import {
    AccountView,
    BuildingView,
    CompanyView,
    DivisionView,
    EditOpportunityView,
    OpportunityProbabilityView,
    OpportunityStatusView,
    SearchDivisionsParameters,
    SearchOpportunitiesParameters
} from '@api-proxies';
import { OpportunityProbabilityService } from '@http/clients/opportunity-probability.service';
import { OpportunityStatusService } from '@http/clients/opportunity-status.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { AwaiterService } from '@services/awaiter.service';
import { FilterStateService } from '@services/filter-state.service';
import { GridService } from '@services/grid.service';
import { DepartmentService } from '@services/http/clients/department.service';
import { UserOrganizationStorageService } from '@services/user-organization-storage.service';
import { CellSorting, ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { FilterValueOpportunities } from '@shared/opportunities-filters/opportunity-filters.config';
import { normalize, onlySortingChanged } from '@utils/filter-list-compare';
import { CellValueChangedEvent, ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { SnackbarNotificationService } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { OPPORTUNITIES_GRID_COLUMNS } from './opportunities-list.config';

const opportunitiesFiltersSpec = {
    searchText: normalize.primitive,
    contactId: normalize.id,
    companyIds: normalize.arrayOf(normalize.id),
    accountIds: normalize.arrayOf(normalize.id),
    buildingIds: normalize.arrayOf(normalize.id),
    companyRoleIds: normalize.arrayOf(normalize.id),
    opportunityStatusIds: normalize.arrayOf(normalize.id),
    divisionIds: normalize.arrayOf(normalize.id),
    salespersonIds: normalize.arrayOf(normalize.id),
    includeArchived: normalize.boolean,
    probabilityValueFrom: normalize.primitive,
    probabilityValueTo: normalize.primitive,
    onlyFavorites: normalize.boolean,
    dueDateFrom: normalize.dateIso,
    dueDateTo: normalize.dateIso,
    potentialValueFrom: normalize.primitive,
    potentialValueTo: normalize.primitive,
    proposedGrossMarginFrom: normalize.primitive,
    proposedGrossMarginTo: normalize.primitive,
    expectedResponseDateFrom: normalize.dateIso,
    expectedResponseDateTo: normalize.dateIso
};

@Component({
    selector: 'app-opportunities-list',
    templateUrl: './opportunities-list.component.html',
    styleUrls: ['./opportunities-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent]
})
export class OpportunitiesListComponent implements OnInit, OnDestroy {
    @Input() filtersKey?: string;
    @Input() accountId?: number;
    @Input() buildingId?: number;
    @Input() companyId?: number;
    @Input() contactId?: number;

    filters?: FilterValueOpportunities;

    opportunityDatasource?: IServerSideDatasource;

    appliedSort?: { sortBy: string; sortDirection: string } | undefined;

    gridColumns = signal<ColDef[]>([...OPPORTUNITIES_GRID_COLUMNS]);

    probabilityItems?: OpportunityProbabilityView[];

    statusItems?: OpportunityStatusView[];

    nonQualifiedStatuses?: OpportunityStatusView[];

    departmentItems?: DivisionView[];

    noResults = signal(false);

    storageKey = signal('');

    // Injected services
    public readonly opportunityService = inject(OpportunityService);
    private readonly filterStateService = inject(FilterStateService<FilterValueOpportunities>);
    private readonly gridService = inject(GridService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly opportunityProbabilityService = inject(OpportunityProbabilityService);
    private readonly opportunityStatusService = inject(OpportunityStatusService);
    private readonly departmentService = inject(DepartmentService);
    private readonly awaiter = inject(AwaiterService);
    private readonly userOrganizationStorageService = inject(UserOrganizationStorageService);

    private filterSubscription?: Subscription;
    private isCreatingDatasource = false;

    async ngOnInit(): Promise<void> {
        const baseKey = this.filtersKey || '';
        const orgId = this.userOrganizationStorageService.getUserOrganization()?.id;
        this.storageKey.set(orgId != null ? `${baseKey}-org-${orgId}` : baseKey);

        if (this.filtersKey) {
            const filtersFromLocalStorage = this.filterStateService.getFromLocalStorage(this.filtersKey);
            this.filters = filtersFromLocalStorage;
        }

        // Wait for initial filters (including URL params) before creating datasource
        this.filterSubscription = this.filterStateService.stateFilters$.subscribe(async (newFilters) => {
            if (!newFilters) return;

            // Prevent race conditions
            if (this.isCreatingDatasource) return;

            // Keep reference to old filters to avoid double API calls on sorting
            // AG-Grid handles sorting internally, but we need to sync with URL parameters
            const oldFilters = this.filters;

            this.filters = { ...newFilters };

            const datasourceExists = this.opportunityDatasource && Object.keys(this.opportunityDatasource).length;
            if (!datasourceExists) {
                // Apply initial sorting from URL only once (on first datasource creation)
                this.updateSort();

                this.isCreatingDatasource = true;
                await this.createDatasource();
            } else if (!onlySortingChanged(oldFilters, newFilters, opportunitiesFiltersSpec)) {
                this.refreshGridView();
            }
        });

        // Load grid configuration
        this.probabilityItems = await this.getProbabilityItems();
        this.statusItems = await this.getStatusItems();
        this.nonQualifiedStatuses = await this.getNonQualifiedStatuses();
        this.departmentItems = await this.getDivision();
        this.setEditorValues();
    }

    ngOnDestroy(): void {
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
        // Reset race condition flag on cleanup
        this.isCreatingDatasource = false;
    }

    async onCellValueChanged(event: CellValueChangedEvent): Promise<void> {
        const { data: rowData, colDef, newValue } = event;
        const fieldName = colDef.field!;

        try {
            const editOpportunity: EditOpportunityView = {
                name: rowData.name,
                dueDate: rowData.dueDate ? new Date(rowData.dueDate) : undefined,
                expectedResponseDate: rowData.expectedResponseDate ? new Date(rowData.expectedResponseDate) : undefined,
                potentialValue: rowData.potentialValue,
                proposedGrossMarginPercentage: rowData.proposedGrossMarginPercentage,
                opportunityProbabilityId: rowData.opportunityProbabilityId,
                opportunityStatusId: rowData.opportunityStatusId,
                divisionId: rowData.divisionId,
                opportunitySalespersons: rowData.opportunitySalespersons ?? []
            };

            this.updateEditPayload(editOpportunity, fieldName, newValue, event);
            await this.opportunityService.edit(rowData.id, editOpportunity);
        } catch (error) {
            this.handleEditError(error, event, fieldName);
        } finally {
            if (fieldName === 'opportunityStatusName') {
                this.setStatusLoading(event, false);
            }
        }
    }

    async onSortChanged(event: CellSorting | null) {
        this.filterStateService.onOrderChanged({
            displayName: event?.displayName,
            order: event?.sortBy
        });
    }

    private refreshGridView(): void {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private searchParamsBuilder(gridParams: any, filters: FilterValueOpportunities): SearchOpportunitiesParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;

        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            filterText: filters?.searchText ?? undefined,
            contactId: this.contactId ?? filters?.contactId,
            companyIds: this.getIds(this.companyId, filters?.companies),
            accountIds: this.getIds(this.accountId, filters?.accounts),
            buildingIds: this.getIds(this.buildingId, filters?.buildings),
            companyRoleIds: filters?.companyRoles?.length ? filters?.companyRoles?.map((c: any) => c.id) : undefined,
            opportunityStatusIds: filters?.statuses?.length ? filters?.statuses.map((s: any) => s.id) : undefined,
            divisionIds: filters?.departments?.length ? filters?.departments.map((d: any) => d.id) : undefined,
            probabilityValueFrom: filters?.probabilityFrom,
            probabilityValueTo: filters?.probabilityTo,
            onlyFavorites: filters?.onlyFavorites,
            dueDateFrom: filters?.dueDateFrom,
            dueDateTo: filters?.dueDateTo,
            potentialValueFrom: filters?.potentialValueFrom,
            potentialValueTo: filters?.potentialValueTo,
            proposedGrossMarginFrom: filters?.proposedGrossMarginFrom,
            proposedGrossMarginTo: filters?.proposedGrossMarginTo,
            includeArchived: filters?.includeArchived ?? undefined,
            expectedResponseDateFrom: filters?.expectedResponseDateFrom,
            expectedResponseDateTo: filters?.expectedResponseDateTo,
            salespersonIds: filters?.salespersons?.length
                ? filters?.salespersons.map((s: any) => s.externalId)
                : undefined
        } as SearchOpportunitiesParameters;

        return searchParams;
    }

    private async createDatasource(): Promise<void> {
        this.opportunityDatasource = {
            getRows: async (params: any) => {
                try {
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(
                        params.request,
                        this.filters ?? ({} as FilterValueOpportunities)
                    );

                    const response = await this.opportunityService.search(searchParams);
                    this.noResults.set(response.results?.length === 0);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                    params.fail();
                    this.noResults.set(true);
                }
            }
        };

        // Reset race condition flag after datasource is created
        this.isCreatingDatasource = false;
    }

    private setStatusLoading(event: CellValueChangedEvent, loading: boolean): void {
        event.data._statusLoading = loading;
        event.api.refreshCells({
            rowNodes: [event.node],
            columns: ['opportunityStatusName']
        });
    }

    private handleEditError(error: any, event: CellValueChangedEvent, fieldName: string): void {
        console.error('Error updating opportunity:', error);
        const message = this.awaiter.processError(error);

        const originalData = { ...event.data };
        if (fieldName === 'proposedGrossMarginPercentage') {
            originalData[fieldName] = event.oldValue / 100;
        } else if (fieldName === 'opportunityStatusName') {
            originalData[fieldName] = event.oldValue;
            if (typeof event.oldValue === 'string') {
                const oldStatus = this.statusItems?.find((s) => s.name === event.oldValue);
                originalData.opportunityStatusId = oldStatus?.id;
            } else if (event.oldValue && typeof event.oldValue === 'object') {
                originalData.opportunityStatusId = event.oldValue.id;
            }
            originalData._statusLoading = false;
        } else {
            originalData[fieldName] = event.oldValue;
        }

        event.node.updateData(originalData);
        this.snackbarNotificationService.error(message ?? 'Error updating opportunity');
    }

    private updateEditPayload(
        editOpportunity: EditOpportunityView,
        fieldName: string,
        newValue: any,
        event: CellValueChangedEvent
    ): void {
        const rowData = event.data;

        switch (fieldName) {
            case 'name':
                editOpportunity.name = newValue;
                break;
            case 'potentialValue':
                editOpportunity.potentialValue = newValue;
                break;
            case 'proposedGrossMarginPercentage':
                editOpportunity.proposedGrossMarginPercentage = newValue != null ? newValue / 100 : undefined;
                break;
            case 'dueDate':
                editOpportunity.dueDate = newValue ? new Date(newValue) : undefined;
                break;
            case 'expectedResponseDate':
                editOpportunity.expectedResponseDate = newValue ? new Date(newValue) : undefined;
                break;
            case 'probabilityValue':
                editOpportunity.opportunityProbabilityId = newValue?.id ?? undefined;
                // Update the row data to keep it in sync
                rowData.opportunityProbabilityId = newValue?.id ?? undefined;
                rowData.probabilityValue = newValue;

                break;
            case 'opportunityStatusName':
                editOpportunity.opportunityStatusId = newValue?.id ?? undefined;
                rowData.opportunityStatusId = newValue?.id ?? undefined;
                rowData.opportunityStatusName = newValue;
                this.setStatusLoading(event, true);
                break;
            case 'divisionName':
                editOpportunity.divisionId = newValue?.id ?? undefined;
                rowData.divisionId = newValue?.id ?? undefined;
                rowData.divisionName = newValue;
                break;
            case 'salesperson':
                editOpportunity.opportunitySalespersons = newValue ?? undefined;
                rowData.opportunitySalespersons = newValue ?? undefined;

                break;
            default:
                throw new Error(`Field ${fieldName} is not configured for editing`);
        }
    }

    private async getProbabilityItems(): Promise<OpportunityProbabilityView[]> {
        return await this.opportunityProbabilityService.getAll();
    }

    private setEditorValues(): void {
        this.setProbabilityEditorValues();
        this.setStatusEditorValues();
        this.setDepartmentEditorValues();
    }

    private setProbabilityEditorValues(): void {
        const columns = [...this.gridColumns()];
        const probabilityCol = columns.find((col) => col.field === 'probabilityValue');
        if (probabilityCol && this.probabilityItems) {
            probabilityCol.cellEditorParams = {
                ...probabilityCol.cellEditorParams,
                values: [
                    { id: null, value: null, label: '-- Clear Selection --' },
                    ...this.probabilityItems.map((item) => ({
                        value: item.probabilityValue,
                        id: item.id,
                        label: item.name ?? `${(item.probabilityValue * 100).toFixed(0)}%`
                    }))
                ],
                valuePlaceholder: 'Select Probability',
                formatValue: (value: any) => {
                    if (value?.label) {
                        return value.label;
                    }

                    if (value === null || value === undefined) {
                        return '';
                    }

                    if (typeof value === 'number') {
                        return `${(value * 100).toFixed(0)}%`;
                    }

                    return value?.toString() || '';
                }
            };

            this.gridColumns.set(columns);
        }
    }

    private async getStatusItems(): Promise<OpportunityStatusView[]> {
        return await this.opportunityStatusService.getAllForEstimate();
    }
    private async getNonQualifiedStatuses(): Promise<OpportunityStatusView[]> {
        return await this.opportunityStatusService.getNonQualifiedStatuses();
    }

    private setStatusEditorValues(): void {
        const columns = [...this.gridColumns()];
        const opportunityStatusCol = columns.find((col) => col.field === 'opportunityStatusName');

        if (!opportunityStatusCol || !this.statusItems || !this.nonQualifiedStatuses) {
            return;
        }

        const nonQualifiedIds = this.nonQualifiedStatuses.map((s) => s.id);

        opportunityStatusCol.cellEditorParams = (params: any) => {
            const currentStatusId = params.data?.opportunityStatusId;
            const isNonQualified = nonQualifiedIds.includes(currentStatusId);

            const values = isNonQualified ? this.nonQualifiedStatuses : this.statusItems;

            return {
                values: values?.map((item) => ({
                    id: item.id,
                    label: item.name
                }))
            };
        };
        this.gridColumns.set(columns);
    }

    private async getDivision(): Promise<DivisionView[]> {
        return await this.departmentService.search({
            isActive: true
        } as SearchDivisionsParameters);
    }

    private setDepartmentEditorValues(): void {
        const columns = [...this.gridColumns()];
        const departmentCol = columns.find((col) => col.field === 'divisionName');
        if (departmentCol && this.departmentItems) {
            departmentCol.cellEditorParams = {
                ...departmentCol.cellEditorParams,
                values: [
                    { id: null, value: null, label: '-- Clear Selection --' },
                    ...this.departmentItems
                        .filter((item) => !!item.name && item.name.trim() !== '')
                        .map((item) => ({
                            id: item.id,
                            label: item.name
                        }))
                ],
                valuePlaceholder: 'Select Department',
                formatValue: (value: any) => {
                    if (value == null) {
                        return '';
                    }
                    if (typeof value === 'string') {
                        return value;
                    }
                    if (typeof value === 'object') {
                        if (value.label != null) {
                            return String(value.label);
                        }
                    }
                    return '';
                }
            };

            this.gridColumns.set(columns);
        }
    }

    private updateSort(): void {
        if (this.filters?.sortBy && this.filters?.sortDirection) {
            this.appliedSort = {
                sortBy: this.filters.sortBy,
                sortDirection: this.filters.sortDirection
            };
        } else {
            this.appliedSort = undefined;
        }
    }

    private getIds(id?: number, filter?: AccountView[] | CompanyView[] | BuildingView[]): number[] | undefined {
        if (id) {
            return [id];
        }
        if (!filter || filter.length === 0) {
            return undefined;
        }

        return filter.map((filter) => filter.id);
    }
}
